import { useFormik } from 'formik';

import { AddressInformation } from '@frontend/api';
import {
  Button,
  FormControl,
  FormLabel,
  HStack,
  Modal,
  ModalBody,
  ModalButtonGroup,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Radio,
  RadioGroup,
  Skeleton,
  Text,
  VStack,
} from '@frontend/components';

import { AddressDisplay } from 'modules/quotes/quoteDetailsPage/modals/FacilitateOrderDrawer/components/AddressDisplay';

type SuggestedAddressModalProps = {
  isOpen: boolean;
  originalAddress: AddressInformation | null;
  suggestedAddresses: AddressInformation[];
  title?: string;
  onClose: () => void;
  onConfirmAddress: (addressInformation: AddressInformation) => void;
};

export const SuggestedAddressModal = ({
  isOpen,
  originalAddress,
  suggestedAddresses,
  title = 'Verify Address',
  onClose,
  onConfirmAddress,
}: SuggestedAddressModalProps) => {
  const isLoaded = !!originalAddress && suggestedAddresses.length > 0;

  const { values, handleBlur, handleChange, handleSubmit } = useFormik({
    initialValues: { address: 'original' },
    onSubmit: ({ address }) => {
      if (address === 'original' && originalAddress) {
        onConfirmAddress(originalAddress);
      } else {
        const addressIndex = parseInt(address);
        if (!isNaN(addressIndex) && suggestedAddresses[addressIndex]) {
          onConfirmAddress(suggestedAddresses[addressIndex]);
        }
      }
    },
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <form onSubmit={handleSubmit}>
        <ModalContent maxHeight="100%" margin={0}>
          <ModalHeader>
            {title}
            <ModalCloseButton />
          </ModalHeader>
          <ModalBody as={VStack} align="stretch" overflowY="auto">
            <Text>
              To ensure accurate delivery, we suggest the changes highlighted
              below. Please choose which address you would like to use. If
              neither option is correct,&nbsp;
              <Button variant="link" size="md" onClick={onClose}>
                edit your address
              </Button>
              .
            </Text>
            <HStack as={RadioGroup} value={values.address} align="start">
              <FormControl>
                <FormLabel>Original Address</FormLabel>
                <Skeleton isLoaded={!!originalAddress}>
                  <Radio
                    name="address"
                    value="original"
                    onBlur={handleBlur}
                    onChange={handleChange}
                  >
                    <AddressDisplay addressInformation={originalAddress} />
                  </Radio>
                </Skeleton>
              </FormControl>
              <FormControl>
                <FormLabel width="max-content">Suggested Addresses</FormLabel>
                <Skeleton
                  isLoaded={suggestedAddresses.length > 0}
                  display="flex"
                  flexDirection="column"
                  gap={1}
                >
                  {suggestedAddresses.map((address, index) => (
                    <Radio
                      // eslint-disable-next-line react/no-array-index-key
                      key={index}
                      name="address"
                      value={`${index}`}
                      onBlur={handleBlur}
                      onChange={handleChange}
                    >
                      <AddressDisplay addressInformation={address} />
                    </Radio>
                  ))}
                </Skeleton>
              </FormControl>
            </HStack>
          </ModalBody>
          <ModalFooter>
            <ModalButtonGroup>
              <Button variant="secondary" onClick={onClose}>
                Cancel
              </Button>
              <Button isDisabled={!isLoaded} type="submit">
                Use this address
              </Button>
            </ModalButtonGroup>
          </ModalFooter>
        </ModalContent>
      </form>
    </Modal>
  );
};
